import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CheckCircleIcon,
  Cluster,
  DocumentIcon,
  EyeIcon,
  FeatureShowcase,
  Field,
  InsightCard,
  Input,
  LockIcon,
  Page,
  QueryState,
  ShieldIcon,
  Stack,
  StatStrip,
  TextArea,
  Toggle,
} from '@integra/ui';
import {
  useAdaptationOptions,
  useBrandSafetyRules,
  useCompliancePack,
  useUpdateAdaptationOptions,
  useUpdateBrandSafetyRules,
  useUpdateCompliancePack,
} from '../api/hooks.js';

/** Starter compliance packs for common regulated industries (§21). */
const TEMPLATES: Record<string, { industry: string; disclaimers: string[]; claims: string[] }> = {
  Finance: {
    industry: 'finance',
    disclaimers: ['Capital at risk. The value of investments can go down as well as up.'],
    claims: ['guaranteed returns', 'risk-free', 'get rich quick'],
  },
  Pharma: {
    industry: 'pharma',
    disclaimers: ['Always read the label. Use only as directed.'],
    claims: ['miracle cure', 'no side effects', 'cures'],
  },
  Gambling: {
    industry: 'gambling',
    disclaimers: ['18+. Please gamble responsibly. BeGambleAware.org'],
    claims: ['guaranteed win', 'risk-free bet', "can't lose"],
  },
};

/** One term per line; blank lines and surrounding whitespace are dropped. */
function toLines(terms: string[]): string {
  return terms.join('\n');
}
function fromText(text: string): string[] {
  return text
    .split('\n')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/**
 * Brand-safety rules (§21): the per-workspace ruleset that governs what may go out.
 * Prohibited terms HARD-BLOCK a publish (single or broadcast) at the door; review
 * terms warn without blocking. Unfinished-content markers (lorem ipsum, TODO, …)
 * are always on and need no configuration. This is the config end of the gate you
 * already see on the asset page.
 */
export function BrandSafetyPage() {
  const { workspaceId = '' } = useParams();
  const rules = useBrandSafetyRules(workspaceId);
  const update = useUpdateBrandSafetyRules(workspaceId);

  const adaptation = useAdaptationOptions(workspaceId);
  const updateAdaptation = useUpdateAdaptationOptions(workspaceId);

  const [prohibited, setProhibited] = useState('');
  const [review, setReview] = useState('');

  // Seed the editor from the server once the ruleset loads.
  useEffect(() => {
    if (rules.data !== undefined) {
      setProhibited(toLines(rules.data.prohibitedTerms));
      setReview(toLines(rules.data.reviewTerms));
    }
  }, [rules.data]);

  function save() {
    update.mutate({ prohibitedTerms: fromText(prohibited), reviewTerms: fromText(review) });
  }

  const compliance = useCompliancePack(workspaceId);
  const updateCompliance = useUpdateCompliancePack(workspaceId);
  const [industry, setIndustry] = useState('');
  const [disclaimers, setDisclaimers] = useState('');
  const [claims, setClaims] = useState('');
  const [appendMode, setAppendMode] = useState(false);

  useEffect(() => {
    if (compliance.data !== undefined) {
      setIndustry(compliance.data.industry ?? '');
      setDisclaimers(toLines(compliance.data.requiredDisclaimers.map((d) => d.text)));
      setClaims(toLines(compliance.data.prohibitedClaims));
      setAppendMode(compliance.data.mode === 'append');
    }
  }, [compliance.data]);

  function saveCompliance() {
    updateCompliance.mutate({
      industry: industry.trim() === '' ? null : industry.trim(),
      requiredDisclaimers: fromText(disclaimers).map((text, i) => ({
        id: `disc-${String(i)}`,
        text,
      })),
      prohibitedClaims: fromText(claims),
      mode: appendMode ? 'append' : 'block',
    });
  }

  function applyTemplate(t: { industry: string; disclaimers: string[]; claims: string[] }): void {
    setIndustry(t.industry);
    setDisclaimers(toLines(t.disclaimers));
    setClaims(toLines(t.claims));
  }

  return (
    <Page
      title="Brand safety & compliance"
      icon={ShieldIcon}
      description="The rules that govern what may go out — brand-safety terms and regulated-industry compliance packs, enforced on every publish and broadcast."
      stats={
        <StatStrip
          stats={[
            {
              icon: ShieldIcon,
              label: 'Prohibited',
              value: fromText(prohibited).length,
              tone: 'accent',
            },
            { icon: EyeIcon, label: 'Review', value: fromText(review).length },
            { icon: DocumentIcon, label: 'Disclosures', value: fromText(disclaimers).length },
            { icon: LockIcon, label: 'Mode', value: appendMode ? 'Append' : 'Block' },
          ]}
        />
      }
    >
      <Stack gap="lg">
        <QueryState loading={rules.isLoading} error={rules.error} what="the brand-safety rules">
          <Stack gap="lg">
            <InsightCard
              title="What's enforced right now"
              meta="Applied on every publish & broadcast"
            >
              {fromText(prohibited).length + fromText(disclaimers).length === 0 ? (
                <span>
                  No hard blocks configured yet. Add prohibited terms or a compliance pack below and
                  every publish is checked against them at the door.
                </span>
              ) : (
                <span>
                  <strong className="font-semibold">{fromText(prohibited).length}</strong>{' '}
                  prohibited term{fromText(prohibited).length === 1 ? '' : 's'} and{' '}
                  <strong className="font-semibold">{fromText(disclaimers).length}</strong> required
                  disclosure{fromText(disclaimers).length === 1 ? '' : 's'} (
                  {appendMode ? 'auto-append' : 'block'} mode) gate every publish;{' '}
                  {fromText(review).length} review term{fromText(review).length === 1 ? '' : 's'}{' '}
                  warn without blocking.
                </span>
              )}
            </InsightCard>

            <FeatureShowcase
              features={[
                {
                  icon: LockIcon,
                  title: 'Prohibited terms',
                  description:
                    "Hard-block a publish at the door — content can't go out until it's fixed.",
                },
                {
                  icon: EyeIcon,
                  title: 'Review terms',
                  description: 'Raise a warning on the pre-flight check without blocking.',
                },
                {
                  icon: CheckCircleIcon,
                  title: 'Unfinished-content markers',
                  description: 'lorem ipsum, TODO, placeholder — always checked, no rule needed.',
                },
              ]}
            />

            <Card
              title="Image adaptation"
              subtitle="How the Adaptation Engine renders your image for each channel."
            >
              <Cluster justify="between" align="center" gap="md">
                <div className="min-w-0">
                  <p className="text-sm text-navy-700">Stamp the workspace logo on renditions</p>
                  <p className="mt-0.5 text-xs text-ink-muted">
                    When on, each channel-fit image is rendered with your primary logo in a safe
                    corner. Off by default &mdash; product and creative imagery is published clean.
                  </p>
                </div>
                <Toggle
                  label="Branded overlay"
                  checked={adaptation.data?.brandedOverlay ?? false}
                  disabled={adaptation.isLoading || updateAdaptation.isPending}
                  onChange={(next) => updateAdaptation.mutate({ brandedOverlay: next })}
                />
              </Cluster>
            </Card>

            <Card
              title="Prohibited terms"
              subtitle="One per line. Any of these present blocks the publish."
            >
              <Field label="Prohibited terms" htmlFor="bs-prohibited">
                <TextArea
                  id="bs-prohibited"
                  rows={6}
                  value={prohibited}
                  placeholder={'guaranteed\nmiracle cure\nrisk-free'}
                  onChange={(e) => setProhibited(e.target.value)}
                />
              </Field>
            </Card>

            <Card
              title="Review terms"
              subtitle="One per line. These warn on the check but never block."
            >
              <Field label="Review terms" htmlFor="bs-review">
                <TextArea
                  id="bs-review"
                  rows={6}
                  value={review}
                  placeholder={'limited offer\nbest\nsale'}
                  onChange={(e) => setReview(e.target.value)}
                />
              </Field>
            </Card>

            <Cluster justify="between" align="center">
              <p className="text-xs text-ink-muted">
                {fromText(prohibited).length} prohibited, {fromText(review).length} review terms.
              </p>
              <Button loading={update.isPending} onClick={save}>
                Save rules
              </Button>
            </Cluster>

            {update.isSuccess && (
              <Alert tone="success" title="Rules saved">
                The gate now applies to every publish and broadcast in this workspace.
              </Alert>
            )}
            {update.error instanceof Error && (
              <Alert tone="error" title="Could not save the rules">
                {update.error.message}
              </Alert>
            )}

            <Card
              title="Compliance pack"
              subtitle="Regulated-industry rules on top of brand safety — mandatory disclosures and forbidden claims, enforced at the publish gate."
            >
              <Stack gap="md">
                <Cluster gap="sm" align="center">
                  <span className="text-xs text-ink-muted">Start from a template:</span>
                  {Object.entries(TEMPLATES).map(([name, t]) => (
                    <Button
                      key={name}
                      size="sm"
                      variant="secondary"
                      onClick={() => applyTemplate(t)}
                    >
                      {name}
                    </Button>
                  ))}
                </Cluster>
                <Field label="Industry (optional)" htmlFor="cp-industry">
                  <Input
                    id="cp-industry"
                    value={industry}
                    placeholder="finance"
                    onChange={(e) => setIndustry(e.target.value)}
                  />
                </Field>
                <Field
                  label="Required disclosures — one per line. Each must appear in published content."
                  htmlFor="cp-disc"
                >
                  <TextArea
                    id="cp-disc"
                    rows={4}
                    value={disclaimers}
                    placeholder={
                      'Capital at risk. The value of investments can go down as well as up.'
                    }
                    onChange={(e) => setDisclaimers(e.target.value)}
                  />
                </Field>
                <Field
                  label="Prohibited claims — one per line. Any present blocks the publish."
                  htmlFor="cp-claims"
                >
                  <TextArea
                    id="cp-claims"
                    rows={3}
                    value={claims}
                    placeholder={'guaranteed returns\nrisk-free'}
                    onChange={(e) => setClaims(e.target.value)}
                  />
                </Field>
                <Cluster justify="between" align="center" gap="md">
                  <Toggle
                    label="Auto-append missing disclosures"
                    checked={appendMode}
                    onChange={setAppendMode}
                  />
                  <Button loading={updateCompliance.isPending} onClick={saveCompliance}>
                    Save compliance pack
                  </Button>
                </Cluster>
                <p className="text-xs text-ink-muted">
                  {appendMode
                    ? 'Append mode: a missing required disclosure is added to the content automatically.'
                    : 'Block mode: a publish is blocked until every required disclosure is present.'}
                </p>
                {updateCompliance.isSuccess && (
                  <Alert tone="success" title="Compliance pack saved">
                    It now governs every publish and broadcast in this workspace.
                  </Alert>
                )}
                {updateCompliance.error instanceof Error && (
                  <Alert tone="error" title="Could not save the pack">
                    {updateCompliance.error.message}
                  </Alert>
                )}
              </Stack>
            </Card>
          </Stack>
        </QueryState>
      </Stack>
    </Page>
  );
}
